import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { num, pct, rupee, signClass } from "../format";
import type { Dashboard as DashboardData, MonthCalendar, StrategyOption } from "../types";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Everything at a glance, for one tradebook or all of them. */
export function Dashboard({
  tradebookId,
  strategies,
  multipleBooks,
}: {
  tradebookId: number;
  strategies: StrategyOption[];
  /** Hides the scope toggle when there is nothing to combine. */
  multipleBooks: boolean;
}) {
  const [allBooks, setAllBooks] = useState(false);
  const scope: number | "all" = allBooks ? "all" : tradebookId;
  const [data, setData] = useState<DashboardData | null>(null);
  const [strategy, setStrategy] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (strategy) params.strategy = strategy;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      setData(await api.dashboard(scope, params));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scope, strategy, range]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <div className="banner error">{error}</div>;
  if (!data) return <div className="muted">Loading dashboard…</div>;

  return (
    <>
      <div className="toolbar">
        {multipleBooks && (
          <div className="field">
            <label>Books</label>
            <select
              value={allBooks ? "all" : "one"}
              onChange={(e) => setAllBooks(e.target.value === "all")}
            >
              <option value="one">Selected book only</option>
              <option value="all">All books combined</option>
            </select>
          </div>
        )}
        <div className="field">
          <label>From</label>
          <input
            type="date"
            value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })}
          />
        </div>
        <div className="field">
          <label>To</label>
          <input
            type="date"
            value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Strategy</label>
          <select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="">All</option>
            {strategies.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="spacer" />
        {loading && <span className="muted">Refreshing…</span>}
      </div>

      {data.flagged_excluded > 0 && (
        <div className="banner warn">
          {data.flagged_excluded} flagged trade{data.flagged_excluded === 1 ? "" : "s"} excluded
          from every P&amp;L figure below — no reliable cost basis.
        </div>
      )}

      <div className="dash-grid">
        <Panel title="Trading Statistics">
          <Row k="Total Trades" v={String(data.total_trades)} />
          <Row k="Winning Trades" v={String(data.winning_trades)} cls="pos" />
          <Row k="Losing Trades" v={String(data.losing_trades)} cls="neg" />
          <Row k="Open Positions" v={String(data.open_trades)} />
          <Row k="Win Rate" v={pct(data.win_rate)} big />
          <Row k="Avg Hold" v={data.avg_holding_days ? `${num(data.avg_holding_days, 1)} days` : "—"} />
          <Row
            k="Trade Frequency"
            v={data.trade_frequency_per_month ? `${num(data.trade_frequency_per_month, 1)} / month` : "—"}
          />
        </Panel>

        <Panel title="Profit / Loss Statistics">
          <div className="grid cols-2">
            <Row k="Realized P&L" v={num(data.realized_pnl)} cls={signClass(data.realized_pnl)} big />
            <Row
              k="Unrealized P&L"
              v={num(data.unrealized_pnl)}
              cls={signClass(data.unrealized_pnl)}
              big
            />
            <Row k="P&L Before Charges" v={num(data.pnl_before_charges)} />
            <Row k="P&L After Charges" v={num(data.pnl_after_charges)} />
            <Row k="Average Win %" v={pct(data.average_win_pct)} cls="pos" />
            <Row k="Average Loss %" v={pct(data.average_loss_pct)} cls="neg" />
            <Row
              k="Biggest Win"
              v={`${num(data.biggest_win)} / ${pct(data.biggest_win_pct)}`}
              cls="pos"
            />
            <Row
              k="Biggest Loss"
              v={`${num(data.biggest_loss)} / ${pct(data.biggest_loss_pct)}`}
              cls="neg"
            />
            <Row k="Expectancy" v={`${num(data.expectancy)} / trade`} cls={signClass(data.expectancy)} />
            <Row k="Max Drawdown" v={num(data.max_drawdown)} cls="neg" />
          </div>
        </Panel>

        <Panel title="Charges">
          <Row k="Total Charges" v={num(data.charges.total ?? 0)} big cls="neg" />
          <div className="grid cols-2">
            <Row k="Brokerage" v={num(data.charges.brokerage ?? 0)} />
            <Row k="GST" v={num(data.charges.gst ?? 0)} />
            <Row k="STT" v={num(data.charges.stt ?? 0)} />
            <Row k="SEBI Charges" v={num(data.charges.sebi ?? 0)} />
            <Row k="Exchange Charges" v={num(data.charges.exchange_txn ?? 0)} />
            <Row k="Stamp Duty" v={num(data.charges.stamp_duty ?? 0)} />
            <Row k="DP Charges" v={num(data.charges.dp_charges ?? 0)} />
          </div>
          <div className="hint">
            Computed from the rate table, not from your contract notes — verify before relying
            on the total.
          </div>
        </Panel>
      </div>

      <div className="dash-grid two">
        <Panel title="Top Winners">
          <BucketTable rows={data.top_winners} empty="No winning trades yet." />
        </Panel>
        <Panel title="Top Losers">
          <BucketTable rows={data.top_losers} empty="No losing trades — yet." />
        </Panel>
      </div>

      <div className="dash-grid two">
        <Panel title="Most Traded">
          <BucketTable rows={data.most_traded} showTrades empty="Nothing closed yet." />
        </Panel>
        <Panel title="Performance by Sector">
          <BucketTable
            rows={data.by_sector}
            showTrades
            empty="No sector data. Upload an instrument list in Settings."
          />
          {data.by_sector.some((s) => s.key === "Unclassified") && (
            <div className="hint">
              Sectors are looked up from market data — NSE files don't include them. You can
              correct any symbol in Settings.
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Performance by Strategy">
        <BucketTable rows={data.by_strategy} showTrades empty="No trades tagged yet." />
      </Panel>

      <Panel title="Equity Curve">
        <Curve points={data.equity_curve} />
      </Panel>

      <div className="dash-grid two">
        <Panel title="Profit Factor">
          <Row k="Gross Profit" v={num(data.gross_profit)} cls="pos" />
          <Row k="Gross Loss" v={num(data.gross_loss)} cls="neg" />
          <Gauge value={data.profit_factor} />
        </Panel>
        <Panel title="Monthly P&L">
          {data.monthly.length === 0 ? (
            <div className="hint">Nothing closed yet.</div>
          ) : (
            <table className="legs">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Trades</th>
                  <th>Win %</th>
                  <th>Net P&L</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly.map((m) => (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td>{m.trades}</td>
                    <td>{pct(m.win_rate, 0)}</td>
                    <td className={signClass(m.net_pnl)}>{num(m.net_pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <TradingCalendar scope={scope} />
    </>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="section-title" style={{ marginTop: 0 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v, cls, big }: { k: string; v: string; cls?: string; big?: boolean }) {
  return (
    <div className="dash-row">
      <span className="k">{k}</span>
      <span className={`v ${cls ?? ""} ${big ? "big" : ""}`}>{v}</span>
    </div>
  );
}

function BucketTable({
  rows,
  showTrades,
  empty,
}: {
  rows: { key: string; trades: number; net_pnl: number; net_pnl_pct: number | null; win_rate: number | null }[];
  showTrades?: boolean;
  empty: string;
}) {
  if (rows.length === 0) return <div className="hint">{empty}</div>;
  return (
    <table className="legs">
      <thead>
        <tr>
          <th>Name</th>
          {showTrades && <th>Trades</th>}
          <th>Net P&L</th>
          <th>Net P&L %</th>
          {showTrades && <th>Win %</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td>
              <strong>{r.key}</strong>
            </td>
            {showTrades && <td>{r.trades}</td>}
            <td className={signClass(r.net_pnl)}>{num(r.net_pnl)}</td>
            <td className={signClass(r.net_pnl_pct)}>{pct(r.net_pnl_pct)}</td>
            {showTrades && <td>{pct(r.win_rate, 0)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Curve({ points }: { points: { date: string; equity: number }[] }) {
  if (points.length < 2) return <div className="hint">Close at least two trades to plot a curve.</div>;

  const width = 960;
  const height = 220;
  const pad = 10;
  const values = points.map((p) => p.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const x = (i: number) => pad + (i / (points.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.equity)}`).join(" ");
  const last = values[values.length - 1];
  const stroke = last >= values[0] ? "var(--accent)" : "var(--loss)";

  return (
    <>
      <div className="dash-row">
        <span className="k">Current</span>
        <span className={`v big ${signClass(last - values[0])}`}>{rupee(last, 0)}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: 220 }}>
        <path
          d={`${path} L ${x(points.length - 1)} ${y(min)} L ${x(0)} ${y(min)} Z`}
          fill={stroke}
          opacity={0.08}
        />
        <path d={path} fill="none" stroke={stroke} strokeWidth={2} />
        <circle cx={x(points.length - 1)} cy={y(last)} r={3.5} fill={stroke} />
      </svg>
    </>
  );
}

function Gauge({ value }: { value: number | null }) {
  // A profit factor above ~1.5 is healthy; the arc saturates at 3 so a single
  // enormous ratio doesn't make everything else look identical.
  const capped = Math.min(value ?? 0, 3);
  const fraction = capped / 3;
  const radius = 70;
  const circumference = Math.PI * radius;
  const colour = (value ?? 0) >= 1 ? "var(--accent)" : "var(--loss)";

  return (
    <div style={{ textAlign: "center", padding: "10px 0" }}>
      <svg viewBox="0 0 180 100" style={{ width: 200, height: 110 }}>
        <path
          d="M 20 90 A 70 70 0 0 1 160 90"
          fill="none"
          stroke="var(--border)"
          strokeWidth={14}
          strokeLinecap="round"
        />
        <path
          d="M 20 90 A 70 70 0 0 1 160 90"
          fill="none"
          stroke={colour}
          strokeWidth={14}
          strokeLinecap="round"
          strokeDasharray={`${fraction * circumference} ${circumference}`}
        />
      </svg>
      <div className={`v big ${(value ?? 0) >= 1 ? "pos" : "neg"}`}>
        {value === null ? "—" : num(value)}
      </div>
      <div className="k">Profit Factor</div>
      {value === null && <div className="hint">No losing trades yet, so there's no ratio.</div>}
    </div>
  );
}

function TradingCalendar({ scope }: { scope: number | "all" }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<MonthCalendar | null>(null);

  useEffect(() => {
    api
      .monthCalendar(scope, year, month)
      .then(setData)
      .catch(() => setData(null));
  }, [scope, year, month]);

  const step = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  if (!data) return null;

  const cells: (number | null)[] = [
    ...Array.from({ length: data.first_weekday }, () => null),
    ...Array.from({ length: data.days_in_month }, (_, i) => i + 1),
  ];
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    const week = cells.slice(i, i + 7);
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const iso = (d: number) =>
    `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 12 }}>
        <div className="section-title" style={{ margin: 0 }}>
          Trading Calendar
        </div>
        <div className="right row">
          <span className="muted">
            {MONTHS[month - 1]} {year} ·{" "}
            <strong className={signClass(data.month_pnl)}>{rupee(data.month_pnl, 0)}</strong>{" "}
            ({data.month_trades} trade{data.month_trades === 1 ? "" : "s"})
          </span>
          <button className="ghost" onClick={() => step(-1)}>
            ‹
          </button>
          <button className="ghost" onClick={() => step(1)}>
            ›
          </button>
        </div>
      </div>

      <table className="calendar">
        <thead>
          <tr>
            {WEEKDAYS.map((d) => (
              <th key={d}>{d}</th>
            ))}
            <th>Week</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => {
            // Typed explicitly: the array holds nulls for padding days, which
            // would otherwise widen the accumulator to number | null.
            const weekPnl = week.reduce<number>(
              (a, d) => a + (d ? (data.days[iso(d)]?.net_pnl ?? 0) : 0),
              0,
            );
            const weekTrades = week.reduce<number>(
              (a, d) => a + (d ? (data.days[iso(d)]?.trades ?? 0) : 0),
              0,
            );
            return (
              <tr key={wi}>
                {week.map((d, di) => {
                  const cell = d ? data.days[iso(d)] : undefined;
                  return (
                    <td key={di} className={cell ? (cell.net_pnl >= 0 ? "win" : "loss") : ""}>
                      {d && (
                        <>
                          <div className="date">{d}</div>
                          <div className={`amount ${cell ? signClass(cell.net_pnl) : "muted"}`}>
                            {cell ? rupee(cell.net_pnl, 0) : "—"}
                          </div>
                          {cell && (
                            <div className="k">
                              {cell.trades} trade{cell.trades === 1 ? "" : "s"}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
                <td className="week-total">
                  <div className={`amount ${signClass(weekPnl)}`}>
                    {weekTrades ? rupee(weekPnl, 0) : "—"}
                  </div>
                  <div className="k">
                    {weekTrades} trade{weekTrades === 1 ? "" : "s"}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
